import { LobbyHomePages, LobbyHomeTypeEnum } from "../../components/base/homeLobbies/LobbyHomeEnums";
import { CategoriesTypeEnum } from "../../components/liveCasino/enums/CategoryTypeEnum";
import { GameBadgeEnum } from "../../components/liveCasino/enums/GameBadgeEnum";
import config from "../../constants/Configs";
import LobbyTypeEnum, { LobbyPages } from "../../constants/Enums/LobbyTypesEnum";
import { LanguageService } from "../LanguageService";
import { LocalStorageService } from "../LocalStorageService";
import Utils from "../Utils";

const BannerArrowsPosition = {
    TopLeft: 1,
    TopRight: 2,
    BottomLeft: 3,
    BottomRight: 4
}

/**
 * LobbyHelper namespace with methods for working with Local Storage Services.
 */
const LobbyHelper = {
    gamesStatusKey: 'storedGamesV2',
    gamesStatusTTL: 2 * 60 * 60, // 2 hours
    /**
     * store games status locally
     * @param {object} data Games data
     */
    storeGamesStatusLocally(data) {
        /** Removing Old cache*/
        /** ==== remove in next version ==== */
        const oldCache = LocalStorageService.get('storedGames');
        if (oldCache) {
            LocalStorageService.remove('storedGames');
        }
        /** ==== ==== */

        const ttl = LobbyHelper.gamesStatusTTL;
        LocalStorageService.set(LobbyHelper.gamesStatusKey, {
            ...data
        }, { ttl })
    },
    /**
     * Clear stored games locally
     * 
     * */
    clearStoredGamesStatusLocally() {
        LocalStorageService.remove(LobbyHelper.gamesStatusKey);
    },
    /**
     * Method for storing game status locally
     * 
     * */
    storeSingleGameStatusLocally({ likesCount, id }, type) {

        const stored = LobbyHelper.getLocallyStoredGames();
        const config = Object.keys(stored).length ? {} : { ttl: LobbyHelper.gamesStatusTTL };
        switch (type) {
            case 'like':
                LocalStorageService.set(LobbyHelper.gamesStatusKey, {
                    ...stored,
                    [id]: [1, (stored[id] && stored[id][1]) || null, stored[id] && stored[id][2] ? stored[id][2] + 1 : likesCount + 1]
                }, config)
                break;
            case 'unlike':
                LocalStorageService.set(LobbyHelper.gamesStatusKey, {
                    ...stored,
                    [id]: [0, (stored[id] && stored[id][1]) || null, stored[id] && stored[id][2] ? stored[id][2] - 1 : likesCount - 1]
                }, config)
                break;
            case 'favorite':
                LocalStorageService.set(LobbyHelper.gamesStatusKey, {
                    ...stored,
                    [id]: [(stored[id] && stored[id][0]) || null, 1, stored[id] && stored[id][2]]
                }, config)
                break;
            case 'unfavorite':
                LocalStorageService.set(LobbyHelper.gamesStatusKey, {
                    ...stored,
                    [id]: [(stored[id] && stored[id][0]) || null, 0, stored[id] && stored[id][2]]
                }, config)
                break;
        }
    },
    /**
     * Method for getting locally stored games
     * @returns {object} Stored games
     */
    getLocallyStoredGames() {
        return LocalStorageService.get(LobbyHelper.gamesStatusKey) || {}
    },
    /**
     * Method for getting locally stored games by id
     * @param {string} id Game id
     * @returns {object} Stored games
     */
    getLocallyStoredGamesById(id) {
        const stored = LobbyHelper.getLocallyStoredGames();
        const game = {}
        if (stored[id]) {
            if (stored[id][2] && stored[id][2] > 0) {
                game.likesCount = stored[id][2];
            }
            if (stored[id][0] !== null) {
                game.isLiked = stored[id][0];
            }
            if (stored[id] && stored[id][1] !== null) {
                game.isFavorite = stored[id][1];
            }
        }
        return game;
    },

    /**
     * Get category view class name
     * @param {string} categoryView Category view
     * @returns {string} Class name
     * */

    getCategoryViewClassName(categoryView) {
        switch (categoryView) {
            case CategoriesTypeEnum.SlideView:
                return 'tb--categories_slide-view';
            case CategoriesTypeEnum.TabView:
                return 'tb--categories_extend-view';
            case CategoriesTypeEnum.ListView:
                return 'tb--categories_list-view';
        }
        return '';
    },
    /**
     * Get banner arrows class name 
     * @param {string} arrowsPoistion Arrows position
     * @returns {string} Class name
    * */
    getArrowsClassName(arrowsPoistion) {
        switch (arrowsPoistion) {
            case BannerArrowsPosition.TopLeft:
                return "main--slider-top main--slider-left";
            case BannerArrowsPosition.TopRight:
                return "main--slider-top main--slider-right";
            case BannerArrowsPosition.BottomLeft:
                return "main--slider-bottom main--slider-left";
            case BannerArrowsPosition.BottomRight:
                return "main--slider-bottom main--slider-right";
        }
        return '';
    },
    /**
     * Get lobby page class name
     * @param {number} lobbyTape Lobby tape
     * @returns {
     */
    getLobbyFilterClassName(type) {
        switch (type) {
            case 1:
            case 3:
                return "livecasino-filter";
            case 2:
                return "casino-filter"
            default:
                return ''
        }
    },
    /**
     * Get custom badge
     */
    getCustomBadge(el) {
        if (el && el.badge) {
            const badge = el.badge.names && el.badge.names.length > 0 && el.badge.names.find(item => item.languageId === el.languageId)
            const badgeName = badge ? badge.name : el.badge.name
            const customBadge = badgeName ? <span className="tb--custom-badge" style={{ backgroundColor: el.badge.backgroundColor, color: el.badge.textColor }}>{badgeName}</span> : null
            return customBadge
        } else {
            return null
        }
    },

    /**
     * Get data badge
     */
    dataBadge(el) {
        const dataBadge = LobbyHelper.getCustomBadge(el) ? {} : el.badgeTypeId > 0 ? { 'data-badge': LanguageService.getTranslation(`badge${Utils.capitalize(GameBadgeEnum[el.badgeTypeId])}`, 'general') } : {};
        return dataBadge
    },

    /**
     * Get Lobby page banner data
     * @param {string} page Page name 
     * @param {string} type Type of page (home or lobby)
     * @returns {object} Banner data
     */
    getLobbyPageDefaultBanners(page, type = 'lobby') {
        let bannerData = {};

        switch (page) {
            case LobbyTypeEnum[LobbyPages.Tvgames]:
            case LobbyHomeTypeEnum[LobbyHomePages.TvgamesHome]:
                bannerData = {
                    section: type === 'lobby' ? 'tvgames_page_main_section' : 'tv_games_home_secondary_section',
                    defaultMobile: type === 'lobby' ?
                    `${config.DEFAULT_SITE_URL}/img/banners/tv_games_banners/tv_games_mobile.webp`
                        :
                    [`${config.DEFAULT_SITE_URL}/img/banners/lobby_home_banners/lobby_home_mobile_1.webp`, `${config.DEFAULT_SITE_URL}/img/banners/lobby_home_banners/lobby_home_mobile_2.webp`, `${config.DEFAULT_SITE_URL}/img/banners/lobby_home_banners/lobby_home_mobile_3.webp`],
                    defaultDesktop: type === 'lobby' ?
                    `${config.DEFAULT_SITE_URL}/img/banners/tv_games_banners/tv_games.webp`
                        :
                    [`${config.DEFAULT_SITE_URL}/img/banners/lobby_home_banners/lobby_home_desktop_1.webp`, `${config.DEFAULT_SITE_URL}/img/banners/lobby_home_banners/lobby_home_desktop_2.webp`, `${config.DEFAULT_SITE_URL}/img/banners/lobby_home_banners/lobby_home_desktop_3.webp`],   
                };
                break;
            case LobbyTypeEnum[LobbyPages.VirtualSport]:
            case LobbyHomeTypeEnum[LobbyHomePages.VirtualSportHome]:
                bannerData = {
                    section: type === 'lobby' ? 'virtualsport_page_main_section' : 'virtual_sport_home_secondary_section',
                    defaultMobile: type === 'lobby' ?
                    `${config.DEFAULT_SITE_URL}/img/banners/virtual_sport_banners/virtual_sport_mobile.webp`
                        :
                    [`${config.DEFAULT_SITE_URL}/img/banners/lobby_home_banners/lobby_home_mobile_1.webp`, `${config.DEFAULT_SITE_URL}/img/banners/lobby_home_banners/lobby_home_mobile_2.webp`, `${config.DEFAULT_SITE_URL}/img/banners/lobby_home_banners/lobby_home_mobile_3.webp`],
                    defaultDesktop: type === 'lobby' ?
                    `${config.DEFAULT_SITE_URL}/img/banners/virtual_sport_banners/virtual_sport.webp`
                        :
                    [`${config.DEFAULT_SITE_URL}/img/banners/lobby_home_banners/lobby_home_desktop_1.webp`, `${config.DEFAULT_SITE_URL}/img/banners/lobby_home_banners/lobby_home_desktop_2.webp`, `${config.DEFAULT_SITE_URL}/img/banners/lobby_home_banners/lobby_home_desktop_3.webp`],
                }
                break;
            case LobbyTypeEnum[LobbyPages.FastGames]:
            case LobbyHomeTypeEnum[LobbyHomePages.FastGamesHome]:
                bannerData = {
                    section: type === 'lobby' ? 'fastgames_page_main_section' : 'fast_games_home_secondary_section',
                    defaultMobile: type === 'lobby' ? 
                    `${config.DEFAULT_SITE_URL}/img/banners/fast_games_banners/fast_games_mobile.webp`
                        :
                    [`${config.DEFAULT_SITE_URL}/img/banners/lobby_home_banners/lobby_home_mobile_1.webp`, `${config.DEFAULT_SITE_URL}/img/banners/lobby_home_banners/lobby_home_mobile_2.webp`, `${config.DEFAULT_SITE_URL}/img/banners/lobby_home_banners/lobby_home_mobile_3.webp`],
                    defaultDesktop: type === 'lobby' ? 
                    `${config.DEFAULT_SITE_URL}/img/banners/fast_games_banners/fast_games.webp`
                        :
                    [`${config.DEFAULT_SITE_URL}/img/banners/lobby_home_banners/lobby_home_desktop_1.webp`, `${config.DEFAULT_SITE_URL}/img/banners/lobby_home_banners/lobby_home_desktop_2.webp`, `${config.DEFAULT_SITE_URL}/img/banners/lobby_home_banners/lobby_home_desktop_3.webp`],
                }
                break;
            case LobbyTypeEnum[LobbyPages.LiveCasino]:
            case LobbyHomeTypeEnum[LobbyHomePages.LiveCasinoHome]:
                bannerData = {
                    section: type === 'lobby' ? 'livecasino_page_main_section' : 'live_casino_home_secondary_section',
                    defaultMobile: type === 'lobby' ? 
                    `${config.DEFAULT_SITE_URL}/img/banners/live_casino_banners/live_casino_mobile.webp`
                        : 
                    [`${config.DEFAULT_SITE_URL}/img/banners/lobby_home_banners/lobby_home_mobile_1.webp`, `${config.DEFAULT_SITE_URL}/img/banners/lobby_home_banners/lobby_home_mobile_2.webp`, `${config.DEFAULT_SITE_URL}/img/banners/lobby_home_banners/lobby_home_mobile_3.webp`],
                    defaultDesktop:  type === 'lobby' ? 
                    `${config.DEFAULT_SITE_URL}/img/banners/live_casino_banners/live_casino.webp`
                        :
                    [`${config.DEFAULT_SITE_URL}/img/banners/lobby_home_banners/lobby_home_desktop_1.webp`, `${config.DEFAULT_SITE_URL}/img/banners/lobby_home_banners/lobby_home_desktop_2.webp`, `${config.DEFAULT_SITE_URL}/img/banners/lobby_home_banners/lobby_home_desktop_3.webp`],
                }
                break;
            case LobbyTypeEnum[LobbyPages.CasinoLobby]:
            case LobbyHomeTypeEnum[LobbyHomePages.CasinoLobbyHome]:
                bannerData = {
                    section: type === 'lobby' ? 'casino_page_main_section' : 'casino_home_secondary_section',
                    defaultMobile: type === 'lobby' ? 
                        `${config.DEFAULT_SITE_URL}/img/banners/casino_banners/casino_top_banner_mobile.webp` 
                            : 
                        [`${config.DEFAULT_SITE_URL}/img/banners/lobby_home_banners/lobby_home_mobile_1.webp`, `${config.DEFAULT_SITE_URL}/img/banners/lobby_home_banners/lobby_home_mobile_2.webp`, `${config.DEFAULT_SITE_URL}/img/banners/lobby_home_banners/lobby_home_mobile_3.webp`],
                    defaultDesktop: type === 'lobby' ? 
                        `${config.DEFAULT_SITE_URL}/img/banners/casino_banners/casino_top_banner.webp` 
                            : 
                        [`${config.DEFAULT_SITE_URL}/img/banners/lobby_home_banners/lobby_home_desktop_1.webp`, `${config.DEFAULT_SITE_URL}/img/banners/lobby_home_banners/lobby_home_desktop_2.webp`, `${config.DEFAULT_SITE_URL}/img/banners/lobby_home_banners/lobby_home_desktop_3.webp`],
                }
                break;
            default: break;

        }

        return bannerData;
    },
   
  
};

export default LobbyHelper;
